import {
    IFontFamily,
    IMruEntry,
    IFontPickerIconResources,
    IFont,
} from '../FontPicker/FontPicker.types.shared';
import { IGroupedComboBoxItem } from 'grouped-combobox-core';

import { isThemeFont, isFontVariant, isMruEntry } from './fontUtils';
import { iconNames } from './iconNames';
import { canPinFont, getFontAndPinnedState } from './pinning';
import { isInPinnedSection } from './variantSection';

/**
 * Checks if the icon is related to pinning
 */
export function isPinningIcon(iconName: string, iconResource?: IFontPickerIconResources): boolean {
    return (
        iconName === iconResource?.pinnedButtonName ||
        iconName === iconResource?.unpinnedButtonName ||
        iconName === iconNames.pin ||
        iconName === iconNames.pinned
    );
}

/**
 * Checks if the specified font can potentially show icon, either at rest or on hover
 * @param font - The font to check for icon
 * @param isSubscriber - Is this user a subscriber
 */
export function canShowIcon(font: IFont, isSubscriber: boolean): boolean {
    if (isFontVariant(font)) {
        return isSubscriber;
    }

    if (isSubscriber) {
        if (isMruEntry(font)) {
            const { variantType, isThemeFont: isThemeFontLocal } = font;
            return isThemeFontLocal || !!variantType;
        } else {
            // for L1 in subscriber mode, we show Pinned or Unpinned icon on hover.
            return canPinFont(font, isSubscriber);
        }
    } else {
        if (isMruEntry(font)) {
            return isThemeFont(font);
        } else {
            return font.fontType === 'premium';
        }
    }
}

export function fontToIcon(
    item: IGroupedComboBoxItem,
    isSubscriber: boolean,
    isActive: boolean,
    iconResource?: IFontPickerIconResources
): string {
    const [font, isPinned] = getFontAndPinnedState(item);

    if (isPinned && isFontVariant(font)) {
        if (isInPinnedSection(item)) {
            return iconResource?.pinnedButtonName ?? iconNames.pinned;
        }
    }

    // Can use `as IMruEntry` because variantType=undefined is a supported scenario
    const { variantType } = font as IMruEntry;
    if (variantType === 'missing') {
        return iconResource?.missingFontName ?? iconNames.warning;
    }

    if (isActive && isThemeFont(font)) {
        return iconResource?.infoButtonName ?? iconNames.info;
    }

    if (!isSubscriber) {
        // Can use `as IFontFamily` because fontType=undefined is a supported scenario
        const { fontType } = font as IFontFamily;
        return fontType === 'premium'
            ? iconResource?.premiumFontName ?? iconNames.premium
            : iconNames.empty;
    }

    if (isActive) {
        return variantType === 'embedded' || variantType === 'compatible'
            ? iconResource?.infoButtonName ?? iconNames.info
            : isPinned
            ? iconResource?.pinnedButtonName ?? iconNames.pinned
            : iconResource?.unpinnedButtonName ?? iconNames.pin;
    }

    return iconNames.empty;
}
